/**
 * TeamMessageBoard - View team posts and events for athletes
 *
 * Mobile-optimized component for viewing team announcements, events,
 * and participating in discussions via replies.
 */

import { useState, useEffect } from 'react';
import {
  listFeedPosts,
  listFeedReplies,
  createFeedReply,
  deleteFeedReply,
  getEventCalendarUrl,
  type FeedPost,
  type FeedReply,
  useUser,
} from '@tss/shared';
import {
  MessageSquare,
  Calendar,
  MapPin,
  Clock,
  ChevronDown,
  ChevronUp,
  Send,
  Download,
  Megaphone,
  Bell,
  RefreshCw,
  Trash2,
  AlertCircle,
  Pin,
  User,
} from 'lucide-react';

interface TeamMessageBoardProps {
  orgId: string;
  teamId: string;
  proxyAthleteId?: string; // For guardian proxy mode - posts as the athlete
}

// Unified color scheme matching coach app
const POST_COLORS = {
  ANNOUNCEMENT: { bg: '#3b82f6', light: '#eff6ff' },
  UPDATE: { bg: 'var(--color-success)', light: '#f0fdf4' },
  REMINDER: { bg: 'var(--gold-500)', light: '#fff7ed' },
  EVENT: { bg: '#a855f7', light: '#faf5ff' },
  DEFAULT: { bg: '#6b7280', light: '#f9fafb' },
};

export function TeamMessageBoard({ orgId, teamId, proxyAthleteId }: TeamMessageBoardProps) {
  const { userId } = useUser();
  // In proxy mode, use the athlete's ID; otherwise use the guardian/user's ID
  const effectiveUserId = proxyAthleteId || userId;
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPosts();
  }, [orgId, teamId]);

  async function loadPosts(isRefresh = false) {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const { posts: data } = await listFeedPosts(orgId, { team_id: teamId, limit: 50 });
      // Sort pinned posts to top
      const sorted = [...data].sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      setPosts(sorted);
    } catch (err: any) {
      setError(err.message || 'Failed to load messages');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  function handleRefresh() {
    loadPosts(true);
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Header onRefresh={handleRefresh} refreshing={false} />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px',
          }}
        >
          <RefreshCw size={24} className="spin-animation" style={{ color: 'var(--gray-400)' }} />
        </div>
        <style>{spinStyle}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Header onRefresh={handleRefresh} refreshing={refreshing} />
        <div style={{ textAlign: 'center', padding: '32px', color: 'var(--gray-500)' }}>
          <AlertCircle size={32} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
          <p>{error}</p>
          <button
            onClick={() => loadPosts()}
            style={{
              marginTop: '12px',
              padding: '8px 16px',
              fontSize: '14px',
              color: 'var(--purple-600)',
              background: 'transparent',
              border: '1px solid var(--purple-600)',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Header onRefresh={handleRefresh} refreshing={refreshing} />
        <div
          style={{
            textAlign: 'center',
            padding: '40px 24px',
            color: 'var(--gray-500)',
            background: 'white',
            borderRadius: '12px',
            border: '1px solid var(--gray-100)',
          }}
        >
          <MessageSquare size={40} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
          <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
            No messages yet
          </p>
          <p style={{ fontSize: '14px', lineHeight: 1.5 }}>
            Your coach will post announcements, updates, and event details here. Check back soon!
          </p>
        </div>
        <style>{spinStyle}</style>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <Header onRefresh={handleRefresh} refreshing={refreshing} />
      {posts.map((post) => (
        <PostItem
          key={post.post_id}
          post={post}
          orgId={orgId}
          currentUserId={effectiveUserId}
          proxyAthleteId={proxyAthleteId}
          onReplyAdded={() => loadPosts(true)}
        />
      ))}
      <style>{spinStyle}</style>
    </div>
  );
}

// Section header with title and refresh
function Header({ onRefresh, refreshing }: { onRefresh: () => void; refreshing: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '4px 0',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <MessageSquare size={18} style={{ color: 'var(--gray-600)' }} />
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--gray-800)', margin: 0 }}>
          Team Messages
        </h3>
      </div>
      <button
        onClick={onRefresh}
        disabled={refreshing}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '6px 10px',
          fontSize: '13px',
          color: 'var(--purple-600)',
          background: 'transparent',
          border: 'none',
          borderRadius: '6px',
          cursor: refreshing ? 'not-allowed' : 'pointer',
          opacity: refreshing ? 0.6 : 1,
        }}
      >
        <RefreshCw size={14} className={refreshing ? 'spin-animation' : ''} />
        {refreshing ? 'Refreshing...' : 'Refresh'}
      </button>
    </div>
  );
}

function PostItem({
  post,
  orgId,
  currentUserId,
  proxyAthleteId,
  onReplyAdded,
}: {
  post: FeedPost;
  orgId: string;
  currentUserId: string | null;
  proxyAthleteId?: string;
  onReplyAdded: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [replies, setReplies] = useState<FeedReply[]>([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [newReply, setNewReply] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const postType = post.post_type || 'DEFAULT';
  const colors = POST_COLORS[postType as keyof typeof POST_COLORS] || POST_COLORS.DEFAULT;
  const isEvent = postType === 'EVENT';

  async function loadReplies() {
    if (!expanded) return;
    setLoadingReplies(true);
    try {
      const { replies: data } = await listFeedReplies(orgId, post.post_id);
      setReplies(data);
    } catch (err) {
      console.error('Failed to load replies:', err);
    } finally {
      setLoadingReplies(false);
    }
  }

  useEffect(() => {
    if (expanded) {
      loadReplies();
    }
  }, [expanded]);

  async function handleSubmitReply(e: React.FormEvent) {
    e.preventDefault();
    if (!newReply.trim() || submitting) return;

    setSubmitting(true);
    try {
      // Pass athlete_id if in proxy mode so reply is posted as the athlete
      await createFeedReply(orgId, post.post_id, {
        content: newReply.trim(),
        athlete_id: proxyAthleteId,
      });
      setNewReply('');
      loadReplies();
      onReplyAdded();
    } catch (err) {
      console.error('Failed to submit reply:', err);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDeleteReply(replyId: string) {
    if (!confirm('Delete this reply?')) return;
    try {
      await deleteFeedReply(orgId, post.post_id, replyId);
      loadReplies();
    } catch (err) {
      console.error('Failed to delete reply:', err);
    }
  }

  function downloadCalendar() {
    const url = getEventCalendarUrl(orgId, post.post_id);
    window.open(url, '_blank');
  }

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '12px',
        border: post.pinned ? `2px solid ${colors.bg}` : '1px solid var(--gray-100)',
        overflow: 'hidden',
        boxShadow: post.pinned ? `0 2px 8px ${colors.bg}20` : undefined,
      }}
    >
      {/* Pinned Banner */}
      {post.pinned && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 16px',
            background: colors.bg,
            color: 'white',
            fontSize: '12px',
            fontWeight: '500',
          }}
        >
          <Pin size={12} />
          Pinned by Coach
        </div>
      )}

      {/* Post Header */}
      <div
        style={{
          padding: '14px 16px',
          borderBottom: expanded ? '1px solid var(--gray-100)' : 'none',
        }}
      >
        {/* Author + Type Row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '10px',
          }}
        >
          {/* Author Avatar */}
          {post.author_profile_picture ? (
            <img
              src={post.author_profile_picture}
              alt={post.author_name || 'Coach'}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '7px',
                objectFit: 'cover',
                objectPosition: 'center 20%',
                flexShrink: 0,
              }}
            />
          ) : (
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '7px',
                background: 'var(--gray-100)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <User size={16} style={{ color: 'var(--gray-400)' }} />
            </div>
          )}

          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--gray-900)' }}>
                {post.author_name || 'Coach'}
              </span>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '500',
                  background: colors.light,
                  color: colors.bg,
                }}
              >
                <PostIcon type={postType} size={10} color={colors.bg} />
                {getPostLabel(postType)}
              </span>
            </div>
            <span style={{ fontSize: '12px', color: 'var(--gray-400)' }}>
              {formatRelativeTime(post.created_at)}
            </span>
          </div>
        </div>

        {/* Title */}
        {post.title && (
          <h4
            style={{
              fontSize: '15px',
              fontWeight: '600',
              color: 'var(--gray-900)',
              marginBottom: '6px',
              lineHeight: 1.3,
            }}
          >
            {post.title}
          </h4>
        )}

        {/* Content */}
        <p
          style={{
            fontSize: '14px',
            color: 'var(--gray-700)',
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            margin: 0,
          }}
        >
          {post.content}
        </p>

        {/* Event Details */}
        {isEvent && post.event_start && (
          <div
            style={{
              marginTop: '12px',
              padding: '12px',
              background: colors.light,
              borderRadius: '10px',
              borderLeft: `3px solid ${colors.bg}`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <Clock size={14} style={{ color: colors.bg }} />
              <span style={{ fontSize: '13px', fontWeight: '500', color: 'var(--gray-800)' }}>
                {formatEventDate(post.event_start)}
                {post.event_end && ` – ${formatEventTime(post.event_end)}`}
              </span>
            </div>
            {post.location && (
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}
              >
                <MapPin size={14} style={{ color: colors.bg }} />
                <span style={{ fontSize: '13px', color: 'var(--gray-700)' }}>{post.location}</span>
              </div>
            )}
            <button
              onClick={downloadCalendar}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                background: colors.bg,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              <Download size={14} />
              Add to Calendar
            </button>
          </div>
        )}

        {/* Reply Toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginTop: '12px',
            padding: '8px 12px',
            fontSize: '13px',
            fontWeight: '500',
            color: 'var(--purple-600)',
            background: 'var(--gray-50)',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            width: '100%',
            justifyContent: 'center',
          }}
        >
          <MessageSquare size={14} />
          {(post.reply_count || 0) === 0
            ? 'Be the first to reply'
            : `${post.reply_count} ${post.reply_count === 1 ? 'reply' : 'replies'}`}
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      {/* Replies Section */}
      {expanded && (
        <div style={{ padding: '12px 16px', background: 'var(--gray-50)' }}>
          {loadingReplies ? (
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <RefreshCw
                size={16}
                className="spin-animation"
                style={{ color: 'var(--gray-400)' }}
              />
            </div>
          ) : (
            <>
              {replies.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    marginBottom: '12px',
                  }}
                >
                  {replies.map((reply) => (
                    <div
                      key={reply.reply_id}
                      style={{
                        display: 'flex',
                        gap: '10px',
                        padding: '10px 12px',
                        background: 'white',
                        borderRadius: '10px',
                        border: '1px solid var(--gray-100)',
                      }}
                    >
                      {/* Reply Author Avatar */}
                      {reply.author_profile_picture ? (
                        <img
                          src={reply.author_profile_picture}
                          alt={reply.author_name || 'User'}
                          style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '6px',
                            objectFit: 'cover',
                            objectPosition: 'center 20%',
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '6px',
                            background: 'var(--gray-100)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <User size={14} style={{ color: 'var(--gray-400)' }} />
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '2px',
                          }}
                        >
                          <span
                            style={{
                              fontSize: '13px',
                              fontWeight: '600',
                              color: 'var(--gray-800)',
                            }}
                          >
                            {reply.author_name || 'Team Member'}
                          </span>
                          <span style={{ fontSize: '11px', color: 'var(--gray-400)' }}>
                            {formatRelativeTime(reply.created_at)}
                          </span>
                        </div>
                        <p
                          style={{
                            fontSize: '13px',
                            color: 'var(--gray-700)',
                            lineHeight: 1.4,
                            margin: 0,
                          }}
                        >
                          {reply.content}
                        </p>
                      </div>
                      {reply.author_id === currentUserId && (
                        <button
                          onClick={() => handleDeleteReply(reply.reply_id)}
                          style={{
                            padding: '4px',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--gray-400)',
                            alignSelf: 'flex-start',
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Reply Form */}
              <form onSubmit={handleSubmitReply} style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={newReply}
                  onChange={(e) => setNewReply(e.target.value)}
                  placeholder="Write a reply..."
                  style={{
                    flex: 1,
                    padding: '12px 14px',
                    border: '1px solid var(--gray-200)',
                    borderRadius: '10px',
                    fontSize: '14px',
                    background: 'white',
                  }}
                />
                <button
                  type="submit"
                  disabled={!newReply.trim() || submitting}
                  style={{
                    padding: '12px 16px',
                    background: 'var(--purple-600)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: newReply.trim() && !submitting ? 'pointer' : 'not-allowed',
                    opacity: newReply.trim() && !submitting ? 1 : 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Send size={18} />
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function PostIcon({ type, size, color }: { type: string; size: number; color: string }) {
  const iconStyle = { color };
  switch (type) {
    case 'EVENT':
      return <Calendar size={size} style={iconStyle} />;
    case 'ANNOUNCEMENT':
      return <Megaphone size={size} style={iconStyle} />;
    case 'REMINDER':
      return <Bell size={size} style={iconStyle} />;
    case 'UPDATE':
      return <MessageSquare size={size} style={iconStyle} />;
    default:
      return <MessageSquare size={size} style={iconStyle} />;
  }
}

function getPostLabel(type: string): string {
  switch (type) {
    case 'EVENT':
      return 'Event';
    case 'ANNOUNCEMENT':
      return 'Announcement';
    case 'REMINDER':
      return 'Reminder';
    case 'UPDATE':
      return 'Update';
    default:
      return 'Post';
  }
}

function formatEventDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatEventTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const spinStyle = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  .spin-animation {
    animation: spin 1s linear infinite;
  }
`;
